'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import type { EmblaViewportRefType } from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { SPARTAN_CARD_IMAGE_PRELOAD_OFFSET } from '@/app/lcv/components/sections/spartan/shared/image-loading';

export interface SpartanCarouselImageLoading {
  loading: 'eager' | 'lazy';
  fetchPriority?: 'low';
}

interface UseCarouselImageLoadingOptions {
  emblaApi?: EmblaCarouselType;
  emblaRef: EmblaViewportRefType;
  itemCount: number;
}

const areIndicesEqual = (first: number[], second: number[]) =>
  first.length === second.length &&
  first.every((value, index) => value === second[index]);

export const useCarouselImageLoading = ({
  emblaApi,
  emblaRef,
  itemCount,
}: UseCarouselImageLoadingOptions) => {
  const { ref: setInViewRef, inView: isNearViewport } = useInView({
    rootMargin: SPARTAN_CARD_IMAGE_PRELOAD_OFFSET,
    threshold: 0,
    triggerOnce: true,
    initialInView: false,
  });
  const [visibleIndices, setVisibleIndices] = useState<number[]>([0]);
  const [requestedIndices, setRequestedIndices] = useState<Set<number>>(
    () => new Set()
  );

  const updateVisibleIndices = useCallback(
    (api: EmblaCarouselType) => {
      const nextIndices = Array.from(
        new Set([...api.slidesInView(), api.selectedScrollSnap()])
      )
        .filter((index) => index >= 0 && index < itemCount)
        .sort((first, second) => first - second);

      setVisibleIndices((currentIndices) =>
        areIndicesEqual(currentIndices, nextIndices)
          ? currentIndices
          : nextIndices
      );
    },
    [itemCount]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const handleUpdate = () => updateVisibleIndices(emblaApi);

    handleUpdate();
    emblaApi.on('select', handleUpdate);
    emblaApi.on('settle', handleUpdate);
    emblaApi.on('reInit', handleUpdate);

    return () => {
      emblaApi.off('select', handleUpdate);
      emblaApi.off('settle', handleUpdate);
      emblaApi.off('reInit', handleUpdate);
    };
  }, [emblaApi, updateVisibleIndices]);

  useEffect(() => {
    if (!isNearViewport || itemCount === 0) return;

    setRequestedIndices((currentIndices) => {
      const nextIndices = new Set(currentIndices);

      visibleIndices.forEach((index) => {
        nextIndices.add(index);
        if (index > 0) nextIndices.add(index - 1);
        if (index + 1 < itemCount) nextIndices.add(index + 1);
      });

      return nextIndices.size === currentIndices.size
        ? currentIndices
        : nextIndices;
    });
  }, [isNearViewport, itemCount, visibleIndices]);

  const currentVisibleIndices = useMemo(
    () => new Set(visibleIndices),
    [visibleIndices]
  );
  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      setInViewRef(node);
      emblaRef(node);
    },
    [emblaRef, setInViewRef]
  );
  const getImageLoading = useCallback(
    (index: number): SpartanCarouselImageLoading => {
      if (!requestedIndices.has(index)) return { loading: 'lazy' };
      if (currentVisibleIndices.has(index)) return { loading: 'eager' };

      return { loading: 'eager', fetchPriority: 'low' };
    },
    [currentVisibleIndices, requestedIndices]
  );

  return {
    getImageLoading,
    isNearViewport,
    setViewportRef,
  };
};
